import React, { useState, useEffect } from "react";
import { Bell } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { enUS, fr } from 'date-fns/locale';
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

export type Notification = {
  id: string;
  type: 'new_request' | 'status_change' | 'reminder';
  title: string;
  message: string;
  read: boolean;
  created_at: string;
  service_request_id?: string;
};

export const NotificationsDropdown = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();

  const unreadCount = notifications.filter(n => !n.read).length;

  // Get the appropriate locale for date-fns based on current language
  const getDateFnsLocale = () => {
    switch (i18n.language) {
      case 'fr':
        return fr;
      case 'en':
      default:
        return enUS;
    }
  };

  useEffect(() => {
    fetchNotifications();
    
    // Subscribe to new notifications
    const channel = supabase
      .channel('notifications_channel')
      .on(
        'postgres_changes',
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'notifications' 
        },
        (payload) => {
          console.log('New notification received:', payload);
          
          const newNotification: Notification = {
            id: payload.new.id,
            type: payload.new.type,
            title: payload.new.title,
            message: payload.new.message,
            read: payload.new.read,
            created_at: payload.new.created_at,
            service_request_id: payload.new.service_request_id
          };
          
          setNotifications(prev => [newNotification, ...prev]);
        }
      )
      .on(
        'postgres_changes',
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'service_requests' 
        },
        (payload) => {
          const newNotification: Notification = {
            id: payload.new.id,
            type: 'new_request',
            title: t('common.newServiceRequest'),
            message: `New request for ${payload.new.service_name || 'your service'}`,
            read: false,
            created_at: payload.new.created_at,
            service_request_id: payload.new.id
          };
          
          setNotifications(prev => [newNotification, ...prev]);
        }
      )
      .on(
        'postgres_changes',
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'service_requests' 
        },
        (payload) => {
          // Only create notification if status changed
          if (payload.old.status !== payload.new.status) {
            const newNotification: Notification = {
              id: `${payload.new.id}-${Date.now()}`,
              type: 'status_change',
                          title: t('common.requestStatusUpdated'),
            message: `Request status changed to ${payload.new.status}`,
              read: false,
              created_at: new Date().toISOString(),
              service_request_id: payload.new.id
            };
            
            setNotifications(prev => [newNotification, ...prev]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // First, try to fetch notifications from the notifications table
      const { data: notificationsData, error: notificationsError } = await (supabase
        .from('notifications' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20) as any);
      
      if (notificationsError) {
        console.error('Error fetching notifications:', notificationsError);
        // If there's an error with the notifications table, fall back to service requests
        fallbackToServiceRequests(user.id);
        return;
      }
      
      if (notificationsData && notificationsData.length > 0) {
        // Map the notifications to the expected format
        const mappedNotifications: Notification[] = notificationsData.map((notification: any) => ({
          id: notification.id,
          type: notification.type,
          title: notification.title,
          message: notification.message,
          read: notification.read,
          created_at: notification.created_at,
          service_request_id: notification.service_request_id
        }));
        
        setNotifications(mappedNotifications);
      } else {
        // If no notifications in the table, fall back to service requests
        fallbackToServiceRequests(user.id);
      }
    } catch (error) {
      console.error('Error in fetchNotifications:', error);
      // In case of any other error, try the fallback
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        fallbackToServiceRequests(user.id);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fallback method to create notifications from service requests
  const fallbackToServiceRequests = async (userId: string) => {
    try {
      // Fetch the 10 most recent service requests as notifications
      const { data, error } = await supabase
        .from('service_requests')
        .select('id, customer_name, status, created_at, services(name)')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      const mappedNotifications: Notification[] = data.map(request => ({
        id: request.id,
        type: 'new_request',
        title: t('common.newServiceRequest'),
        message: `Request from ${request.customer_name} for ${request.services?.name || 'your service'}`,
        read: false, // For simplicity, treating all as unread initially
        created_at: request.created_at,
        service_request_id: request.id
      }));

      setNotifications(mappedNotifications);
    } catch (error) {
      console.error('Error in fallback notification fetching:', error);
      setNotifications([]);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      // First check if the notification exists in the notifications table
      const { data: notificationData } = await (supabase
        .from('notifications' as any)
        .select('id')
        .eq('id', notificationId)
        .maybeSingle() as any);
        
      if (notificationData) {
        // If it exists in the database, update it there
        await (supabase
          .from('notifications' as any)
          .update({ read: true })
          .eq('id', notificationId) as any);
      }
      
      // Always update the state for UI
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true } 
            : notification
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    if (notification.service_request_id) {
      navigate(`/dashboard/service-requests?id=${notification.service_request_id}`);
    }
    setOpen(false);
  };

  const markAllAsRead = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      // Update all unread notifications in the database
      await (supabase
        .from('notifications' as any)
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false) as any);
        
      // Update state for UI
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true }))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-red-500 text-white text-xs min-w-[18px] flex items-center justify-center"
            >
              {unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex justify-between items-center p-2 border-b">
          <h3 className="font-semibold">{t('navigation.notifications')}</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs h-7 px-2"
              onClick={markAllAsRead}
            >
              {t('common.markAllAsRead')}
            </Button>
          )}
        </div>
        
        <div className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-sm text-gray-500">
              {t('common.loadingNotifications')}
            </div>
          ) : notifications.length > 0 ? (
            notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={`p-3 cursor-pointer ${!notification.read ? 'bg-gray-50' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex flex-col w-full gap-1">
                  <div className="flex justify-between items-start">
                    <span className="font-medium">{notification.title}</span>
                    <span className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(notification.created_at), { 
                        addSuffix: true,
                        locale: getDateFnsLocale()
                      })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{notification.message}</p>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full absolute right-3 top-3" />
                  )}
                </div>
              </DropdownMenuItem>
            ))
          ) : (
            <div className="p-4 text-center text-sm text-gray-500">
              {t('common.noNotifications')}
            </div>
          )}
        </div>
        
        <div className="p-2 border-t text-center">
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-xs w-full"
            onClick={() => navigate('/dashboard/service-requests')}
          >
            {t('common.viewAllRequests')}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
